import { supabase } from './supabaseClient'
import type { MockInterviewQuestion, MockInterviewSession, QuizCategory, QuizDifficulty } from '../types'

/**
 * Generate open-ended interview questions via Edge Function
 */
export async function generateInterviewQuestions(params: {
  applicationId: string | null
  generalRole?: string
  generalTechStack?: string[]
  difficulty: QuizDifficulty
  questionCount: number
  categories: QuizCategory[]
}): Promise<MockInterviewQuestion[]> {
  const { data: { user }, error: userError } = await supabase.auth.getUser()

  if (userError || !user) {
    throw new Error('No active session. Please log in again.')
  }

  const { data, error } = await supabase.functions.invoke<{ questions: MockInterviewQuestion[] }>(
    'generate-mock-interview',
    { body: params }
  )

  if (error) {
    const msg = (error as { message?: string }).message || 'Failed to generate interview questions'
    throw new Error(msg)
  }

  if (!data?.questions) {
    throw new Error('No questions returned from AI')
  }

  return data.questions
}

/**
 * Evaluate a candidate's answer to an interview question
 */
export async function evaluateAnswer(params: {
  question: string
  userAnswer: string
  positionTitle: string
  techStack: string[]
  difficulty: QuizDifficulty
  category: QuizCategory
  followUpHint?: string
}): Promise<{
  score: number
  feedback: string
  strengths: string[]
  improvements: string[]
  sample_answer_hint: string
}> {
  const { data, error } = await supabase.functions.invoke('evaluate-interview-answer', {
    body: params,
  })

  if (error) {
    const msg = (error as { message?: string }).message || 'Failed to evaluate answer'
    throw new Error(msg)
  }

  if (!data || typeof data.score !== 'number') {
    throw new Error('Invalid evaluation response')
  }

  return data
}

/**
 * Convert text to speech audio via Edge Function
 * Returns an audio Blob that can be played with new Audio(URL.createObjectURL(blob))
 */
export async function textToSpeech(
  text: string,
  voice?: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer'
): Promise<Blob> {
  const { data: { session } } = await supabase.auth.getSession()

  if (!session) {
    throw new Error('No active session. Please log in again.')
  }

  // Call the Edge Function directly via fetch to get binary audio response
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const response = await fetch(`${supabaseUrl}/functions/v1/text-to-speech`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ text, voice }),
  })

  if (!response.ok) {
    let errorMsg = 'TTS failed'
    try {
      const errorData = await response.json()
      errorMsg = errorData.error || errorMsg
    } catch {
      // ignore parse errors
    }
    throw new Error(errorMsg)
  }

  const blob = await response.blob()
  return blob
}

// Track the currently playing audio so it can be stopped
let currentAudio: HTMLAudioElement | null = null

/**
 * Stop any audio that is currently playing
 */
export function stopCurrentAudio(): void {
  if (currentAudio) {
    currentAudio.pause()
    currentAudio.currentTime = 0
    if (currentAudio.src) {
      URL.revokeObjectURL(currentAudio.src)
    }
    currentAudio = null
  }
}

/**
 * Play an audio blob and return a promise that resolves when playback finishes
 */
export function playAudioBlob(blob: Blob): Promise<void> {
  // Stop any previously playing audio
  stopCurrentAudio()

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const audio = new Audio(url)
    currentAudio = audio

    audio.onended = () => {
      URL.revokeObjectURL(url)
      if (currentAudio === audio) currentAudio = null
      resolve()
    }
    audio.onerror = () => {
      URL.revokeObjectURL(url)
      if (currentAudio === audio) currentAudio = null
      reject(new Error('Audio playback failed'))
    }
    audio.play().catch(err => {
      URL.revokeObjectURL(url)
      if (currentAudio === audio) currentAudio = null
      reject(err)
    })
  })
}

/**
 * Save a completed mock interview session
 */
export async function saveMockInterviewSession(
  userId: string,
  applicationId: string | null,
  generalRole: string | null,
  generalTech: string[] | null,
  difficulty: QuizDifficulty,
  categories: QuizCategory[],
  questions: MockInterviewQuestion[],
  score: number,
  durationSeconds: number | null
): Promise<MockInterviewSession> {
  const { data, error } = await supabase
    .from('mock_interview_sessions')
    .insert({
      user_id: userId,
      application_id: applicationId,
      general_role: generalRole,
      general_tech: generalTech,
      difficulty,
      categories,
      total_questions: questions.length,
      score,
      questions,
      duration_seconds: durationSeconds,
    })
    .select()
    .single()

  if (error) {
    console.error('Save mock interview error:', error)
    throw new Error(`Failed to save session: ${error.message}`)
  }

  return data
}

/**
 * Fetch all mock interview sessions for a user
 */
export async function fetchMockInterviewSessions(userId: string): Promise<MockInterviewSession[]> {
  const { data, error } = await supabase
    .from('mock_interview_sessions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Fetch mock interviews error:', error)
    throw new Error(`Failed to fetch sessions: ${error.message}`)
  }

  return data || []
}

/**
 * Fetch a single mock interview session by ID
 */
export async function fetchMockInterviewById(
  sessionId: string,
  userId: string
): Promise<MockInterviewSession | null> {
  const { data, error } = await supabase
    .from('mock_interview_sessions')
    .select('*')
    .eq('id', sessionId)
    .eq('user_id', userId)
    .single()

  if (error) {
    console.error('Fetch mock interview error:', error)
    return null
  }

  return data
}

/**
 * Fetch total count of mock interview sessions (for free tier gating)
 */
export async function fetchMockInterviewCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('mock_interview_sessions')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)

  if (error) {
    console.error('Count mock interviews error:', error)
    return 0
  }

  return count || 0
}

/**
 * Fetch count of mock interview sessions in the last 7 days (for Pro weekly gating)
 */
export async function fetchWeeklyMockInterviewCount(userId: string): Promise<number> {
  const sevenDaysAgo = new Date()
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

  const { count, error } = await supabase
    .from('mock_interview_sessions')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('created_at', sevenDaysAgo.toISOString())

  if (error) {
    console.error('Weekly count mock interviews error:', error)
    return 0
  }

  return count || 0
}
